import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { ConfirmationDialog } from '../components/Common/Dialogs';

type Proceed = () => void;

interface NavigationGuardContextValue {
  /** True while an operation (e.g. a save) is in progress and navigation should be guarded. */
  isBlocked: boolean;
  /** Enable or disable the navigation guard. */
  setBlocked: (blocked: boolean) => void;
  /**
   * Attempt a navigation. If the guard is active the user is prompted with a
   * leave/stay dialog and `proceed` only runs if they confirm; otherwise it runs immediately.
   */
  requestNavigation: (proceed: Proceed) => void;
}

const NavigationGuardContext = createContext<NavigationGuardContextValue | undefined>(undefined);

export const NavigationGuardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const blockedRef = useRef(false);
  // Hold the pending navigation in a ref so it is read synchronously, avoiding an
  // impure state updater (which React StrictMode double-invokes) that could fire it twice.
  const pendingRef = useRef<Proceed | null>(null);

  const setBlocked = useCallback((blocked: boolean) => {
    blockedRef.current = blocked;
    setIsBlocked(blocked);
  }, []);

  const requestNavigation = useCallback((proceed: Proceed) => {
    if (blockedRef.current) {
      pendingRef.current = proceed;
      setDialogOpen(true);
    } else {
      proceed();
    }
  }, []);

  const confirmLeave = useCallback(() => {
    const proceed = pendingRef.current;
    pendingRef.current = null;
    setDialogOpen(false);
    proceed?.();
  }, []);

  const stay = useCallback(() => {
    pendingRef.current = null;
    setDialogOpen(false);
  }, []);

  const value = useMemo(
    () => ({ isBlocked, setBlocked, requestNavigation }),
    [isBlocked, setBlocked, requestNavigation],
  );

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
      <ConfirmationDialog
        open={dialogOpen}
        onClose={stay}
        onConfirm={confirmLeave}
        title="Leave before saving finishes?"
        message="Your change is still saving. If you leave now, you won't see whether it saved successfully."
        confirmText="Leave"
        cancelText="Stay"
      />
    </NavigationGuardContext.Provider>
  );
};

/**
 * Access the navigation guard. Returns a passthrough fallback when used outside a provider
 * so components (and their tests) render safely without one.
 */
export const useNavigationGuard = (): NavigationGuardContextValue => {
  const ctx = useContext(NavigationGuardContext);
  const noopSetBlocked = useCallback(() => {}, []);
  const passthroughNavigate = useCallback((proceed: Proceed) => proceed(), []);
  return ctx ?? { isBlocked: false, setBlocked: noopSetBlocked, requestNavigation: passthroughNavigate };
};
